export function useStoryblokUrl(initialLink?: StoryblokMultilink | string): string {
    if (!initialLink) {
        return '';
    }
    const resolvableLink =
        typeof initialLink !== 'string' && isResolvableStoryblokLink(initialLink as unknown) ? initialLink : null;
    if (resolvableLink) {
        try {
            const { lang } = useLanguage();
            const resolvedLink = resolveStoryblokLink(resolvableLink, useCountryLanguages(), lang.value);
            const anchor = resolvableLink.anchor ? `#${resolvableLink.anchor}` : '';
            return anchor ? `${resolvedLink.replace(/\/$/, '')}${anchor}` : resolvedLink;
        } catch {
            // Fall back to the legacy URL handling for incomplete Storyblok link data.
        }
    }

    const unresolvedLink = initialLink as StoryblokMultilink | string;
    const unresolvedStory =
        typeof unresolvedLink !== 'string' && 'story' in unresolvedLink
            ? (unresolvedLink.story as { full_slug?: string } | null | undefined)
            : null;
    const link: string =
        typeof unresolvedLink === 'string' ? unresolvedLink : unresolvedStory?.full_slug || unresolvedLink.cached_url;
    const resolveUrl = ensureLinkFormat(removeFirstLanguagePrefix(link || ''));

    if (typeof unresolvedLink === 'string') {
        return resolveUrl;
    }

    const anchor = unresolvedLink?.anchor ? `#${unresolvedLink.anchor}` : '';

    if (unresolvedLink.linktype === 'url') {
        return `${resolveUrl}${anchor}`;
    } else if (unresolvedLink.linktype === 'story') {
        if (anchor) {
            if (resolveUrl.endsWith('/')) {
                return `${resolveUrl.slice(0, -1)}${anchor}`;
            } else {
                return `${resolveUrl}${anchor}`;
            }
        }

        return resolveUrl;
    } else if (unresolvedLink.linktype === 'email') {
        return `mailto:${link}`;
    } else if (unresolvedLink.linktype === 'asset') {
        return link;
    }
    return resolveUrl;
}

const ensureLinkFormat = (url: string): string => {
    const { urlSegment } = useLanguage();

    if (
        !url.startsWith('http') &&
        !url.startsWith('www.') &&
        !url.startsWith(`/${urlSegment.value}`) &&
        !url.startsWith('mailto:') &&
        !url.startsWith('tel:')
    ) {
        url = url.replace(/^\//, '');
        url = url.split('/').slice(1).join('/');
        return `/${urlSegment.value}/${url}`;
    } else if (url.startsWith('www.')) {
        url = `https://${url}`;
    }

    return url;
};

const removeFirstLanguagePrefix = (url: string): string => {
    const { country } = useLanguage();
    const countryLanguages = useCountryLanguages();
    const availableCountries = Object.keys(countryLanguages);
    //add availableLanguages because the prefix can be a country or a language
    const availableLanguages = countryLanguages[country.value].availableLanguages;
    const possiblePrefixes = Array.from(new Set([...availableCountries, ...availableLanguages]));
    const regEx = new RegExp(`^/?(${possiblePrefixes.join('|')})?/((${possiblePrefixes.join('|')})?/.*)`);

    if (possiblePrefixes.length && regEx.test(url)) {
        return url.replace(regEx, '/$2');
    }
    return url;
};
